import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import { getTranslatedField } from '../utils/translationUtils';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface LocationState {
  service: {
    name: string;
    name_et?: string;
  };
  date: string;
  time: string;
}

const GuestBookingSuccessPage: React.FC = () => {
  const location = useLocation();
  const { t, language } = useI18n();
  useDocumentTitle(t.booking.success);
  const state = location.state as LocationState;

  if (!state) {
    // Redirect to home if state is not available
    return <Link to="/" />;
  }
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString(language === 'et' ? 'et-EE' : 'en-US', options);
  };

  return (
    <div className="py-12 max-w-2xl mx-auto px-4 text-center">
      <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h1 className="mt-4 text-3xl font-bold text-gray-900">
        {t.booking.success || 'Booking Successful!'}
      </h1>
      <p className="mt-2 text-lg text-gray-600">
        {t.booking.guestBookingSuccess || 'Your appointment has been confirmed. A confirmation email has been sent to you.'}
      </p>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6 text-left">
        <h3 className="text-lg font-semibold mb-4">{t.booking.bookingDetails}</h3>
        <div className="space-y-3 text-gray-700">
          <div className="flex justify-between py-2 border-b">
            <span>{t.booking.serviceLabel}</span>
            <span className="font-medium">{getTranslatedField(state.service, 'name', language)}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span>{t.booking.dateLabel}</span>
            <span className="font-medium">{formatDate(state.date)}</span>
          </div>
          <div className="flex justify-between py-2">
            <span>{t.booking.timeLabel}</span>
            <span className="font-medium">{state.time}</span>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <Link
          to="/"
          className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
        >
          {t.common.goHome || 'Back to Home'}
        </Link>
      </div>
    </div>
  );
};

export default GuestBookingSuccessPage;